import { RevenueHistory, RevenueItem } from '../services/revenuehistory/revenueHistoryService';

/**
 * Shared add-ons helpers. Standalone module to avoid circular imports — consumed by
 * revenue utils, charts, and cost-price utils. Replaces the old
 * `revenueChoicesFlatten` synthetic category; choices stay nested on each
 * `RevenueItem`. Everything here is a pure read-only aggregator.
 */

/** Sum of a revenue item's add-on (choice) revenue, in the requested VAT mode. */
export function getItemAddOnsRevenue(item: RevenueItem, showExVat: boolean): number {
  return (item.choices ?? []).reduce(
    (sum, c) => sum + (showExVat ? (c.exVat || 0) : (c.price || 0)),
    0
  );
}

/** Item base revenue + all of its add-ons. */
export function getItemTotalRevenue(item: RevenueItem, showExVat: boolean): number {
  const base = showExVat ? (item.exVat || 0) : (item.price || 0);
  return base + getItemAddOnsRevenue(item, showExVat);
}

export interface AddOnAggregation {
  name: string;
  quantity: number;
  revenue: number;
}

/**
 * That item's choices, merged by EXACT name, sorted by revenue desc.
 * Used in the per-item Add-ons popover in the revenue history items view.
 */
export function getItemChoiceBreakdown(item: RevenueItem, showExVat: boolean): AddOnAggregation[] {
  const map = new Map<string, AddOnAggregation>();
  for (const choice of item.choices ?? []) {
    const existing = map.get(choice.name);
    const revenue = showExVat ? (choice.exVat || 0) : (choice.price || 0);
    if (existing) {
      existing.quantity += choice.quantity || 0;
      existing.revenue += revenue;
    } else {
      map.set(choice.name, { name: choice.name, quantity: choice.quantity || 0, revenue });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
}

/**
 * Every choice across every record/category/item, merged by EXACT name and sorted
 * by revenue desc. Includes €0 rows — this is the merge-by-name kernel of the
 * old flatten, now exposed as a pure read-only aggregator.
 */
export function aggregateAddOns(
  revenueHistory: RevenueHistory[],
  showExVat: boolean
): AddOnAggregation[] {
  const map = new Map<string, AddOnAggregation>();
  for (const record of revenueHistory) {
    for (const category of record.revenueData?.categories ?? []) {
      for (const item of category.items ?? []) {
        for (const choice of item.choices ?? []) {
          const existing = map.get(choice.name);
          const revenue = showExVat ? (choice.exVat || 0) : (choice.price || 0);
          if (existing) {
            existing.quantity += choice.quantity || 0;
            existing.revenue += revenue;
          } else {
            map.set(choice.name, { name: choice.name, quantity: choice.quantity || 0, revenue });
          }
        }
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
}

/** True iff any revenue item in the given period has at least one choice. */
export function hasAnyChoices(revenueHistory: RevenueHistory[]): boolean {
  for (const record of revenueHistory) {
    for (const category of record.revenueData?.categories ?? []) {
      for (const item of category.items ?? []) {
        if ((item.choices?.length ?? 0) > 0) return true;
      }
    }
  }
  return false;
}
